import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterCapture = ({ source = "insights", compact = false }) => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/newsletter`, { email, source, website });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail?.[0]?.msg || err?.message || "Could not subscribe");
    }
  };

  if (compact) {
    return (
      <div className="bg-paper-cool rounded-md p-8 md:p-10 border border-black/5" data-testid="newsletter-compact">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-2">
              [ Monthly compliance digest ]
            </div>
            <h3 className="font-display font-semibold text-[1.5rem] md:text-[1.75rem] tracking-tightest leading-tight">
              Practical HVAC and compliance notes — once a month, no fluff.
            </h3>
          </div>
          <div className="md:col-span-5">
            {status === "done" ? (
              <div className="flex items-center gap-3 p-4 bg-white rounded-sm border border-black/5" data-testid="newsletter-success">
                <span className="h-7 w-7 bg-accent rounded-full flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4 text-white" strokeWidth={3} />
                </span>
                <div>
                  <div className="text-[14px] font-semibold text-ink">You’re on the list.</div>
                  <div className="text-[12px] text-muted-foreground">We’ll be in touch soon.</div>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
                <input
                  type="email"
                  required
                  placeholder="you@company.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white border border-black/10 rounded-sm px-4 py-3 text-[14px] text-ink placeholder:text-muted/60 focus:outline-none focus:border-accent transition-colors"
                  data-testid="newsletter-email"
                />
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="absolute -left-[9999px]"
                  aria-hidden="true"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="newsletter-submit"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-accent text-white font-semibold text-[13px] rounded-sm hover:bg-brand transition-colors disabled:opacity-60"
                >
                  {status === "sending" ? "…" : "Subscribe"}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </form>
            )}
            {status === "error" && (
              <p className="mt-2 text-[12px] text-accent font-mono" data-testid="newsletter-error">{error}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full / banner variant
  return (
    <section className="py-24 bg-ink text-white" data-testid="newsletter-banner">
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-4">
            [ Monthly compliance digest ]
          </div>
          <h3 className="font-display font-bold text-[2rem] md:text-[3rem] tracking-tightest leading-[1.05] text-white">
            Practical HVAC + compliance notes —
            <span className="text-accent"> once a month, no fluff.</span>
          </h3>
        </div>
        <div className="lg:col-span-5">
          {status === "done" ? (
            <div className="bg-white/5 border border-white/15 rounded-sm p-6" data-testid="newsletter-success">
              <div className="font-display font-semibold text-[1.15rem] mb-1">You’re on the list.</div>
              <div className="text-[14px] text-white/60">We’ll send you our next compliance digest.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                placeholder="you@company.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/5 border border-white/15 rounded-sm px-4 py-3.5 text-[14px] text-white placeholder:text-white/40 focus:outline-none focus:border-accent transition-colors"
                data-testid="newsletter-email"
              />
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="absolute -left-[9999px]"
                aria-hidden="true"
              />
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="newsletter-submit"
                className="inline-flex items-center gap-2 px-5 py-3.5 bg-accent text-white font-semibold text-[14px] rounded-sm hover:bg-brand transition-colors disabled:opacity-60"
              >
                {status === "sending" ? "…" : "Subscribe"}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </form>
          )}
          {status === "error" && (
            <p className="mt-2 text-[12px] text-accent font-mono" data-testid="newsletter-error">{error}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterCapture;
